// ============================================================================
// UNIFIED REPORT ENGINE — MERGE TAG REGISTRY
// ============================================================================
// Every document type advertises which merge tags are available. The
// template editor uses these to populate the tag picker, and the PDF
// generator replaces them at render time.
// ============================================================================

import {
  type DocumentType,
  type MergeTag,
} from './types';

// ============================================================================
// TAG BUILDERS
// ============================================================================

function tag(tagStr: string, label: string, category: string, example?: string): MergeTag {
  return { tag: `{{ ${tagStr} }}`, label, category, example };
}

// ============================================================================
// COMMON TAG SETS
// ============================================================================

/** Tags available in every document */
const COMMON_TAGS: MergeTag[] = [
  tag('company_name',      'Company Name',        'Company',  'Acme Corp'),
  tag('company_email',     'Company Email',       'Company',  'info@acme.co.za'),
  tag('company_phone',     'Company Phone',       'Company',  '+27 11 123 4567'),
  tag('company_address',   'Company Address',     'Company',  '123 Main St, Johannesburg'),
  tag('company_reg_no',    'Registration Number', 'Company',  '2020/123456/07'),
  tag('company_vat_no',    'VAT Number',          'Company',  '4123456789'),
  tag('current_date',      'Current Date',        'System',   '2026-03-15'),
  tag('current_time',      'Current Time',        'System',   '14:30'),
  tag('generated_by',      'Generated By',        'System',   'John Smith'),
  tag('page_number',       'Page Number',         'System',   '1'),
  tag('total_pages',       'Total Pages',         'System',   '3'),
];

/** Contact-related tags (finance docs, ops cards) */
const CONTACT_TAGS: MergeTag[] = [
  tag('contact_name',      'Contact Name',        'Contact',  'Jane Doe'),
  tag('contact_email',     'Contact Email',       'Contact',  'jane@client.co.za'),
  tag('contact_phone',     'Contact Phone',       'Contact',  '+27 82 123 4567'),
  tag('contact_address',   'Contact Address',     'Contact',  '456 Client Road'),
  tag('contact_company',   'Contact Company',     'Contact',  'Client Inc'),
  tag('contact_vat_no',    'Contact VAT Number',  'Contact',  '4987654321'),
];

/** Finance document tags (invoice, quote, bill, etc.) */
const FINANCE_DOC_TAGS: MergeTag[] = [
  tag('document_number',   'Document Number',     'Document', 'INV-2026-0042'),
  tag('document_type',     'Document Type',       'Document', 'Invoice'),
  tag('document_date',     'Document Date',       'Document', '2026-03-01'),
  tag('due_date',          'Due Date',            'Document', '2026-03-31'),
  tag('reference',         'Reference',           'Document', 'PO-12345'),
  tag('po_number',         'PO Number',           'Document', 'PO-12345'),
  tag('subtotal',          'Subtotal',            'Totals',   'R 10,000.00'),
  tag('discount_total',    'Total Discount',      'Totals',   'R 500.00'),
  tag('tax_total',         'Total Tax',           'Totals',   'R 1,425.00'),
  tag('grand_total',       'Grand Total',         'Totals',   'R 10,925.00'),
  tag('amount_paid',       'Amount Paid',         'Totals',   'R 5,000.00'),
  tag('balance_due',       'Balance Due',         'Totals',   'R 5,925.00'),
  tag('currency',          'Currency',            'Document', 'ZAR'),
  tag('payment_terms',     'Payment Terms',       'Document', 'Net 30'),
  tag('status',            'Status',              'Document', 'Sent'),
];

/** Bank-related tags */
const BANK_TAGS: MergeTag[] = [
  tag('bank_name',         'Bank Name',           'Banking',  'FNB'),
  tag('bank_account_name', 'Account Name',        'Banking',  'Acme Corp'),
  tag('bank_account_no',   'Account Number',      'Banking',  '62123456789'),
  tag('bank_account_number','Account Number',     'Banking',  '62123456789'),
  tag('bank_branch_code',  'Branch Code',         'Banking',  '250655'),
  tag('bank_account_type', 'Account Type',        'Banking',  'Business Current'),
  tag('bank_swift',        'SWIFT Code',          'Banking',  'FIRNZAJJ'),
  tag('bank_reference',    'Payment Reference',   'Banking',  'INV-2026-0042'),
];

/** Form submission tags */
const FORM_TAGS: MergeTag[] = [
  tag('form_title',        'Form Title',          'Form',      'Safety Inspection'),
  tag('submission_date',   'Submission Date',      'Form',      '2026-03-15'),
  tag('submission_time',   'Submission Time',      'Form',      '14:30'),
  tag('submitted_by',      'Submitted By',        'Form',      'John Smith'),
  tag('submission_status', 'Status',              'Form',      'Completed'),
  tag('form_version',      'Form Version',        'Form',      'v2.1'),
  tag('submission_id',     'Submission ID',        'Form',      'SUB-20260315-001'),
];

/** Certificate tags */
const CERTIFICATE_TAGS: MergeTag[] = [
  tag('certificate_number', 'Certificate Number', 'Certificate', 'CERT-2026-0042'),
  tag('recipient_name',     'Recipient Name',     'Certificate', 'John Smith'),
  tag('issued_date',        'Date Issued',        'Certificate', '2026-03-15'),
  tag('expiry_date',        'Expiry Date',        'Certificate', '2027-03-15'),
  tag('score',              'Score',              'Certificate', '92%'),
  tag('grade',              'Grade',              'Certificate', 'A'),
];

/** Operations / job card / work order tags */
const OPS_TAGS: MergeTag[] = [
  tag('job_number',         'Job Number',          'Operations', 'JOB-2026-0042'),
  tag('work_order_number',  'Work Order Number',   'Operations', 'WO-2026-0015'),
  tag('site_name',          'Site Name',           'Operations', 'Warehouse A'),
  tag('site_address',       'Site Address',        'Operations', '789 Industrial Blvd'),
  tag('assigned_to',        'Assigned To',         'Operations', 'Mike Johnson'),
  tag('priority',           'Priority',            'Operations', 'High'),
  tag('job_status',         'Job Status',          'Operations', 'In Progress'),
  tag('scheduled_date',     'Scheduled Date',      'Operations', '2026-03-15'),
  tag('completed_date',     'Completed Date',      'Operations', '2026-03-15'),
  tag('total_hours',        'Total Hours',         'Operations', '8.5'),
  tag('total_materials',    'Material Cost',       'Operations', 'R 2,500.00'),
];

/** Attendance / shift tags */
const ATTENDANCE_TAGS: MergeTag[] = [
  tag('employee_name',      'Employee Name',       'Employee',   'Sarah Williams'),
  tag('employee_number',    'Employee Number',     'Employee',   'EMP-042'),
  tag('department',         'Department',          'Employee',   'Operations'),
  tag('shift_name',         'Shift Name',          'Attendance', 'Day Shift'),
  tag('shift_date',         'Shift Date',          'Attendance', '2026-03-15'),
  tag('clock_in',           'Clock In',            'Attendance', '07:00'),
  tag('clock_out',          'Clock Out',           'Attendance', '17:00'),
  tag('total_shift_hours',  'Total Hours',         'Attendance', '10.0'),
  tag('location',           'Location',            'Attendance', 'Site Alpha'),
];

const VEHICLE_INSPECTION_TAGS: MergeTag[] = [
  tag('inspection_type',        'Inspection Type',        'Inspection', 'Pre-Trip'),
  tag('inspection_date',        'Inspection Date',        'Inspection', '2026-03-15'),
  tag('inspection_status',      'Inspection Status',      'Inspection', 'Completed'),
  tag('overall_condition',      'Overall Condition',      'Inspection', 'Good'),
  tag('inspector_name',         'Inspector Name',         'Inspection', 'Fleet Supervisor'),
  tag('vehicle_registration',   'Vehicle Registration',   'Vehicle',    'CA 123456'),
  tag('vehicle_name',           'Vehicle Name',           'Vehicle',    'Ford Ranger'),
  tag('vehicle_make',           'Vehicle Make',           'Vehicle',    'Ford'),
  tag('vehicle_model',          'Vehicle Model',          'Vehicle',    'Ranger'),
  tag('assigned_driver',        'Assigned Driver',        'Vehicle',    'Chris Driver'),
  tag('odometer_reading',       'Odometer Reading',       'Inspection', '182,450 km'),
  tag('fuel_level',             'Fuel Level',             'Inspection', 'Half'),
  tag('issue_count',            'Issue Count',            'Inspection', '2'),
  tag('issues_found',           'Issues Found',           'Inspection', 'Worn front tyre · Cracked mirror'),
  tag('ai_overall_risk',        'AI Overall Risk',        'AI Review',  'Medium'),
  tag('ai_executive_summary',   'AI Executive Summary',   'AI Review',  'Minor body damage with follow-up required'),
  tag('ai_recommended_actions', 'AI Recommended Actions', 'AI Review',  'Replace tyre · Repair mirror housing'),
];

/** HR document tags */
const HR_TAGS: MergeTag[] = [
  tag('employee_name',      'Employee Name',       'Employee',   'Sarah Williams'),
  tag('employee_number',    'Employee Number',     'Employee',   'EMP-042'),
  tag('id_number',          'ID Number',           'Employee',   '8801015009082'),
  tag('tax_number',         'Tax Number',          'Employee',   '0123456789'),
  tag('department',         'Department',          'Employee',   'Operations'),
  tag('job_title',          'Job Title',           'Employee',   'Technician'),
  tag('pay_period',         'Pay Period',          'Payroll',    'March 2026'),
  tag('pay_date',           'Pay Date',            'Payroll',    '2026-03-25'),
  tag('gross_pay',          'Gross Pay',           'Payroll',    'R 25,000.00'),
  tag('total_deductions',   'Total Deductions',    'Payroll',    'R 6,500.00'),
  tag('net_pay',            'Net Pay',             'Payroll',    'R 18,500.00'),
  tag('leave_balance',      'Leave Balance',       'Leave',      '15 days'),
];

const MEETING_TAGS: MergeTag[] = [
  tag('meeting_title', 'Meeting Title', 'Meeting', 'Sprint Planning'),
  tag('meeting_id', 'Meeting ID', 'Meeting', 'meeting-abc123'),
  tag('meeting_status', 'Meeting Status', 'Meeting', 'Waiting to start'),
  tag('host_name', 'Host Name', 'Host', 'Gavin Vermaak'),
  tag('host_role', 'Host Role', 'Host', 'Operations Manager'),
  tag('guest_name', 'Guest Name', 'Guest', 'Jane Guest'),
  tag('scheduled_time', 'Scheduled Time', 'Meeting', '09:00'),
  tag('starts_in', 'Starts In', 'Meeting', '05:00'),
  tag('participant_count', 'Participant Count', 'Meeting', '4'),
  tag('lobby_message', 'Waiting Room Message', 'Waiting Room', 'The host will admit you shortly.'),
  tag('join_button_label', 'Join Button Label', 'Actions', 'Join meeting'),
  tag('login_url', 'Login URL', 'Actions', '/auth/login'),
];

/** Finance report period tags */
const REPORT_PERIOD_TAGS: MergeTag[] = [
  tag('report_title',       'Report Title',        'Report',    'Profit & Loss'),
  tag('period_start',       'Period Start',        'Report',    '2026-01-01'),
  tag('period_end',         'Period End',          'Report',    '2026-03-31'),
  tag('period_label',       'Period Label',        'Report',    'Q1 2026'),
  tag('comparison_period',  'Comparison Period',   'Report',    'Q1 2025'),
];

// ============================================================================
// REPORT-SPECIFIC TAGS
// ============================================================================

const PL_TAGS: MergeTag[] = [
  tag('total_revenue',      'Total Revenue',       'P&L',       'R 500,000.00'),
  tag('total_expenses',     'Total Expenses',      'P&L',       'R 350,000.00'),
  tag('gross_profit',       'Gross Profit',        'P&L',       'R 200,000.00'),
  tag('net_profit',         'Net Profit',          'P&L',       'R 150,000.00'),
  tag('profit_margin',      'Profit Margin',       'P&L',       '30%'),
];

const BALANCE_SHEET_TAGS: MergeTag[] = [
  tag('total_assets',       'Total Assets',        'Balance Sheet', 'R 1,200,000.00'),
  tag('total_liabilities',  'Total Liabilities',   'Balance Sheet', 'R 800,000.00'),
  tag('total_equity',       'Total Equity',        'Balance Sheet', 'R 400,000.00'),
];

const CASH_FLOW_TAGS: MergeTag[] = [
  tag('opening_balance',    'Opening Balance',     'Cash Flow', 'R 100,000.00'),
  tag('total_inflow',       'Total Inflow',        'Cash Flow', 'R 450,000.00'),
  tag('total_outflow',      'Total Outflow',       'Cash Flow', 'R 380,000.00'),
  tag('closing_balance',    'Closing Balance',     'Cash Flow', 'R 170,000.00'),
];

const AGED_TAGS: MergeTag[] = [
  tag('total_owed',         'Total Owed',          'Aged',      'R 250,000.00'),
  tag('current',            'Current',             'Aged',      'R 100,000.00'),
  tag('days_30',            '30 Days',             'Aged',      'R 75,000.00'),
  tag('days_60',            '60 Days',             'Aged',      'R 50,000.00'),
  tag('days_90',            '90 Days',             'Aged',      'R 15,000.00'),
  tag('days_120_plus',      '120+ Days',           'Aged',      'R 10,000.00'),
  tag('days_60_plus',       '60+ Days',            'Aged',      'R 75,000.00'),
];

const VAT_TAGS: MergeTag[] = [
  tag('output_vat',         'Output VAT',          'VAT',       'R 75,000.00'),
  tag('input_vat',          'Input VAT',           'VAT',       'R 52,500.00'),
  tag('net_vat',            'Net VAT Due',         'VAT',       'R 22,500.00'),
];

// ============================================================================
// TYPE → TAG MAPPING
// ============================================================================

const TYPE_SPECIFIC_TAGS: Partial<Record<DocumentType, MergeTag[]>> = {
  // Finance documents
  invoice:           [...CONTACT_TAGS, ...FINANCE_DOC_TAGS, ...BANK_TAGS],
  quote:             [...CONTACT_TAGS, ...FINANCE_DOC_TAGS],
  credit_note:       [...CONTACT_TAGS, ...FINANCE_DOC_TAGS],
  debit_note:        [...CONTACT_TAGS, ...FINANCE_DOC_TAGS],
  supplier_invoice:   [...CONTACT_TAGS, ...FINANCE_DOC_TAGS],
  receipt:           [...CONTACT_TAGS, ...FINANCE_DOC_TAGS],
  statement:         [...CONTACT_TAGS, ...FINANCE_DOC_TAGS, ...BANK_TAGS],
  purchase_order:    [...CONTACT_TAGS, ...FINANCE_DOC_TAGS],
  purchase_requisition: [...CONTACT_TAGS, ...FINANCE_DOC_TAGS],
  delivery_note:     [...CONTACT_TAGS, ...FINANCE_DOC_TAGS],
  waybill:           [...CONTACT_TAGS, ...FINANCE_DOC_TAGS],
  picking_slip:      [...CONTACT_TAGS, ...FINANCE_DOC_TAGS],
  packing_slip:      [...CONTACT_TAGS, ...FINANCE_DOC_TAGS],
  stock_adjustment:  [...FINANCE_DOC_TAGS],
  transfer_order:    [...CONTACT_TAGS, ...FINANCE_DOC_TAGS],
  grn:               [...CONTACT_TAGS, ...FINANCE_DOC_TAGS],
  sales_order:       [...CONTACT_TAGS, ...FINANCE_DOC_TAGS],
  proforma:          [...CONTACT_TAGS, ...FINANCE_DOC_TAGS, ...BANK_TAGS],
  remittance:        [...CONTACT_TAGS, ...FINANCE_DOC_TAGS, ...BANK_TAGS],
  supplier_credit:   [...CONTACT_TAGS, ...FINANCE_DOC_TAGS],
  supplier_quote:    [...CONTACT_TAGS, ...FINANCE_DOC_TAGS],
  rfq:               [...CONTACT_TAGS, ...FINANCE_DOC_TAGS],
  purchase_return:   [...CONTACT_TAGS, ...FINANCE_DOC_TAGS],
  rma:               [...CONTACT_TAGS, ...FINANCE_DOC_TAGS],
  // Finance reports
  profit_loss:               [...REPORT_PERIOD_TAGS, ...PL_TAGS],
  balance_sheet:             [...REPORT_PERIOD_TAGS, ...BALANCE_SHEET_TAGS],
  trial_balance:             [...REPORT_PERIOD_TAGS],
  cash_flow:                 [...REPORT_PERIOD_TAGS, ...CASH_FLOW_TAGS],
  aged_receivables:          [...REPORT_PERIOD_TAGS, ...AGED_TAGS],
  aged_payables:             [...REPORT_PERIOD_TAGS, ...AGED_TAGS],
  vat_report:                [...REPORT_PERIOD_TAGS, ...VAT_TAGS],
  tax_summary:               [...REPORT_PERIOD_TAGS],
  customer_statement:        [...REPORT_PERIOD_TAGS, ...CONTACT_TAGS],
  debtor_reconciliation:     [...REPORT_PERIOD_TAGS],
  creditor_reconciliation:   [...REPORT_PERIOD_TAGS],
  general_ledger:            [...REPORT_PERIOD_TAGS],
  account_transactions:      [...REPORT_PERIOD_TAGS],
  journal_report:            [...REPORT_PERIOD_TAGS],
  sales_by_customer:         [...REPORT_PERIOD_TAGS],
  sales_by_product:          [...REPORT_PERIOD_TAGS],
  purchases_by_supplier:     [...REPORT_PERIOD_TAGS],
  expense_by_category:       [...REPORT_PERIOD_TAGS],
  invoice_summary:           [...REPORT_PERIOD_TAGS],
  bill_summary:              [...REPORT_PERIOD_TAGS],
  // Operations
  job_card:          [...CONTACT_TAGS, ...OPS_TAGS],
  attendance_card:   [...ATTENDANCE_TAGS],
  task_card:         [...CONTACT_TAGS, ...OPS_TAGS],
  service_report:    [...CONTACT_TAGS, ...OPS_TAGS],
  work_order:        [...CONTACT_TAGS, ...OPS_TAGS],
  vehicle_inspection_report: [...VEHICLE_INSPECTION_TAGS],
  // Forms
  form_submission:   [...FORM_TAGS],
  form_certificate:  [...FORM_TAGS, ...CERTIFICATE_TAGS],
  quiz_certificate:  [...FORM_TAGS, ...CERTIFICATE_TAGS],
  // HR
  payslip:              [...HR_TAGS, ...BANK_TAGS],
  employment_contract:  [...HR_TAGS],
  leave_report:         [...HR_TAGS],
  meeting_book_layout:  [...MEETING_TAGS],
  meeting_cancel_page:  [...MEETING_TAGS],
  meeting_join_page:    [...MEETING_TAGS],
  meeting_room_page:    [...MEETING_TAGS],
  meeting_layout:       [...MEETING_TAGS],
  meeting_landing_page: [...MEETING_TAGS],
};

// ============================================================================
// PUBLIC API
// ============================================================================

/**
 * Get all available merge tags for a document type.
 * Returns COMMON + type-specific tags, de-duplicated.
 */
export function getMergeTags(docType: DocumentType): MergeTag[] {
  const specific = TYPE_SPECIFIC_TAGS[docType] || [];
  const all = [...COMMON_TAGS, ...specific];

  // De-duplicate by tag string
  const seen = new Set<string>();
  return all.filter(t => {
    if (seen.has(t.tag)) return false;
    seen.add(t.tag);
    return true;
  });
}

/**
 * Get merge tags grouped by category for display in the tag picker.
 */
export function getMergeTagsByCategory(docType: DocumentType): Array<{
  category: string;
  tags: MergeTag[];
}> {
  const tags = getMergeTags(docType);
  const groups: Record<string, MergeTag[]> = {};

  for (const t of tags) {
    if (!groups[t.category]) groups[t.category] = [];
    groups[t.category].push(t);
  }

  return Object.entries(groups).map(([category, tags]) => ({ category, tags }));
}

/**
 * Replace all merge tags in a string with their values from a data object.
 * Supports both {{ tag }} and {{tag}} formats.
 */
export function replaceMergeTags(
  template: string,
  data: Record<string, string | number | undefined>,
): string {
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) => {
    const value = data[key];
    if (value === undefined || value === null) return match; // leave unreplaced
    return String(value);
  });
}
